import {Tag} from "antd";
import * as styles from "./AiExtractorSelectOptions.module.css";
import AiExtractor, {AiExtractorType, formatAiExtractorType, formatAiExtractorWebSearchConfig} from "../../domain/AiExtractor";
import {formatAiExtractorSource} from "../../domain/AiExtractorSource";
import {ResourceOwnerType} from "../../domain/ResourceOwnerType";
import {orderSystemAndOrganizationOptionGroups} from "../../utils/SelectOptionUtils";

export type AiExtractorSelectOption = {
    label: string;
    value: string;
    aiExtractor: AiExtractor;
};

export type AiExtractorSelectOptionGroup = {
    label: string;
    options: AiExtractorSelectOption[];
};

export function compareAiExtractors(left: AiExtractor, right: AiExtractor) {
    if (left.assigned !== right.assigned) {
        return left.assigned ? -1 : 1;
    }

    return (left.name ?? "").localeCompare(right.name ?? "");
}

export function buildGroupedAiExtractorOptions(aiExtractors: AiExtractor[], systemFirst: boolean): AiExtractorSelectOptionGroup[] {
    const toOption = (aiExtractor: AiExtractor): AiExtractorSelectOption => ({
        label: aiExtractor.name ?? aiExtractor.id ?? "Unknown extractor",
        value: aiExtractor.id ?? "",
        aiExtractor
    });
    const sortedAiExtractors = [...aiExtractors].sort(compareAiExtractors);
    const systemAiExtractors = sortedAiExtractors.filter(aiExtractor => aiExtractor.owner === ResourceOwnerType.SYSTEM);
    const organizationAiExtractors = sortedAiExtractors.filter(aiExtractor => aiExtractor.owner === ResourceOwnerType.ORGANIZATION);
    const organizationGroups = organizationAiExtractors.reduce<Record<string, AiExtractor[]>>(
        (groups, aiExtractor) => {
            const organizationName = aiExtractor.organization?.name ?? "Organization";

            (groups[organizationName] ??= []).push(aiExtractor);

            return groups;
        },
        {}
    );

    const organizationOptionGroups = Object.entries(organizationGroups).map(([organizationName, groupedAiExtractors]) => ({
        label: organizationName,
        options: groupedAiExtractors.map(toOption)
    }));
    const systemOptionGroups = systemAiExtractors.length > 0 ? [{
        label: "System",
        options: systemAiExtractors.map(toOption)
    }] : [];

    return orderSystemAndOrganizationOptionGroups(systemOptionGroups, organizationOptionGroups, systemFirst);
}

export function renderAiExtractorOption(aiExtractor: AiExtractor) {
    const {meta, systemPromptName, supplementaryPromptName} = buildAiExtractorOptionMeta(aiExtractor);

    return (
        <div className={styles.option}>
            <div className={styles.optionTitleRow}>
                <span>{aiExtractor.name}</span>
                {aiExtractor.assigned && (
                    <Tag color="blue" variant={"filled"} title="This AI extractor is assigned to the source.">assigned</Tag>
                )}
                {aiExtractor.managedByInterests && (
                    <Tag color="blue" title="This AI extractor is managed automatically through Interests.">managed</Tag>
                )}
                {systemPromptName && (
                    <Tag title={systemPromptName} className={styles.promptTag}>{systemPromptName}</Tag>
                )}
                {supplementaryPromptName && (
                    <Tag title={supplementaryPromptName} className={styles.promptTag}>+ {supplementaryPromptName}</Tag>
                )}
            </div>
            <div className={styles.optionMeta}>
                {meta}
            </div>
        </div>
    );
}

function buildAiExtractorOptionMeta(aiExtractor: AiExtractor) {
    const modelLabelBase = aiExtractor.model || "-";
    const details = buildAiExtractorDetailParts(aiExtractor);

    const modelLabel = details.length > 0
        ? `${modelLabelBase} (${details.join(", ")})`
        : modelLabelBase;

    const meta = [
        formatAiExtractorType(aiExtractor.type),
        modelLabel,
        formatAiExtractorSource(aiExtractor.source)
    ].filter(Boolean).join(" · ");

    const supplementaryPromptName = aiExtractor.supplementaryPrompt?.name;
    const systemPromptName = aiExtractor.systemPrompt?.name;

    return {
        meta,
        systemPromptName,
        supplementaryPromptName
    };
}

function buildAiExtractorDetailParts(aiExtractor: AiExtractor): string[] {
    const webSearchDetail = formatAiExtractorWebSearchConfig(aiExtractor.webSearchMode, aiExtractor.externalWebSearchBackend);
    const webSearchLabel = webSearchDetail ? `web ${webSearchDetail}` : undefined;

    const detailsMap: Record<AiExtractorType, (string | undefined)[]> = {
        [AiExtractorType.OPENAI_API]: [aiExtractor.openAiServiceTier, aiExtractor.openAiReasoningEffort, webSearchLabel],
        [AiExtractorType.OPENAI_COMPATIBLE_API]: [aiExtractor.aiProviderConnection?.name, aiExtractor.openAiReasoningEffort, webSearchLabel],
        [AiExtractorType.ANTHROPIC_API]: [aiExtractor.anthropicReasoningEffort, webSearchLabel],
        [AiExtractorType.GROK_API]: [aiExtractor.grokReasoningEffort, webSearchLabel],
        [AiExtractorType.OLLAMA]: [webSearchLabel],
        [AiExtractorType.MISTRAL_AI]: [webSearchLabel],
        [AiExtractorType.GEMINI_API]: [webSearchLabel],
    };

    return (detailsMap[aiExtractor.type] || [])
        .filter((value): value is string => !!value)
        .map(value => value.toLowerCase());
}
